/* eslint-disable react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { FiX, FiCheckCircle, FiAlertTriangle, FiLoader } from 'react-icons/fi';
import {
  formatPrice,
  derivePlanTitle,
  derivePlanSubtitle,
  formatDataCapacity,
  formatValidity,
} from '../../utils/esimUtils';
import { ESIM_DOCUMENT_FIELDS } from '../../constants/esimConstants';
import './EsimReviewModal.scss';

function Row({ label, value, fullWidth }) {
  return (
    <div
      className={`esim-review__row ${
        fullWidth ? 'esim-review__row--full' : ''
      }`}
    >
      <span className="esim-review__row-label">{label}</span>
      <span className="esim-review__row-value">{value || '—'}</span>
    </div>
  );
}

Row.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  fullWidth: PropTypes.bool,
};

Row.defaultProps = { value: '', fullWidth: false };

function EsimReviewModal({
  show,
  plan,
  traveler,
  search,
  docs,
  submitting,
  errorMsg,
  onConfirm,
  onHide,
}) {
  if (!plan) return null;
  const total = Number(plan?.totalPrice || plan?.netPrice || 0);
  const currency = plan?.currency || 'INR';

  const uploadedDocs = ESIM_DOCUMENT_FIELDS.filter(
    (f) => docs?.[f.key]?.documentUrl,
  );

  return (
    <Modal
      show={show}
      onHide={submitting ? undefined : onHide}
      size="lg"
      centered
      backdrop={submitting ? 'static' : true}
      keyboard={!submitting}
      dialogClassName="esim-review"
    >
      <Modal.Body>
        {!submitting && (
          <button
            type="button"
            className="esim-review__close"
            onClick={onHide}
            aria-label="Close"
          >
            <FiX />
          </button>
        )}

        <header className="esim-review__head">
          <span className="esim-review__eyebrow">Review &amp; Confirm</span>
          <h3 className="esim-review__title">Confirm your eSIM booking</h3>
          <p className="esim-review__sub">
            Please double-check the details below. Once you confirm, the eSIM
            will be issued to the contact you provided.
          </p>
        </header>

        <section className="esim-review__section">
          <h4 className="esim-review__section-title">Selected Plan</h4>
          <div className="esim-review__plan">
            <div>
              <strong className="esim-review__plan-name">
                {derivePlanTitle(plan)}
              </strong>
              {derivePlanSubtitle(plan) && (
                <p className="esim-review__plan-sub">
                  {derivePlanSubtitle(plan)}
                </p>
              )}
              <div className="esim-review__plan-meta">
                <span>{formatDataCapacity(plan)}</span>
                <span aria-hidden>·</span>
                <span>{formatValidity(plan) || 'Flexible validity'}</span>
              </div>
            </div>
            <div className="esim-review__plan-price">
              <span>
                {formatPrice(total, currency) || `${currency} ${total}`}
              </span>
              <small>incl. taxes</small>
            </div>
          </div>
        </section>

        <section className="esim-review__section">
          <h4 className="esim-review__section-title">Trip</h4>
          <div className="esim-review__rows">
            <Row label="Destination" value={search?.country || ''} />
            <Row label="Travel Start" value={search?.travelStart || ''} />
            <Row label="Travel End" value={search?.travelEnd || ''} />
          </div>
        </section>

        <section className="esim-review__section">
          <h4 className="esim-review__section-title">Traveller</h4>
          <div className="esim-review__rows">
            <Row
              label="Name"
              value={[traveler?.title, traveler?.firstName, traveler?.lastName]
                .filter(Boolean)
                .join(' ')}
            />
            <Row label="Date of Birth" value={traveler?.dateOfBirth} />
            <Row label="Passport Number" value={traveler?.passportNumber} />
            <Row label="Passport Expiry" value={traveler?.passportExpiry} />
            <Row label="Nationality" value={traveler?.nationality} />
            <Row label="Email" value={traveler?.email} />
            <Row label="Mobile" value={traveler?.mobile} />
          </div>
        </section>

        <section className="esim-review__section">
          <h4 className="esim-review__section-title">Documents</h4>
          {uploadedDocs.length === 0 ? (
            <div className="esim-review__docs-empty">
              <FiAlertTriangle />
              No documents have been uploaded yet.
            </div>
          ) : (
            <ul className="esim-review__docs">
              {uploadedDocs.map((field) => (
                <li key={field.key} className="esim-review__doc">
                  <FiCheckCircle />
                  <span>{field.label}</span>
                  <small>{docs[field.key].fileName || 'Uploaded'}</small>
                </li>
              ))}
            </ul>
          )}
        </section>

        {errorMsg && (
          <div className="esim-review__error">
            <FiAlertTriangle />
            <span>{errorMsg}</span>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="esim-review__footer">
        <button
          type="button"
          className="esim-review__cancel"
          onClick={onHide}
          disabled={submitting}
        >
          Edit details
        </button>
        <button
          type="button"
          className="esim-review__confirm"
          onClick={onConfirm}
          disabled={submitting}
        >
          {submitting ? (
            <>
              <FiLoader className="esim-review__spin" />
              Booking eSIM…
            </>
          ) : (
            <>
              <FiCheckCircle />
              Confirm &amp; Pay{' '}
              {formatPrice(total, currency) || `${currency} ${total}`}
            </>
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

EsimReviewModal.propTypes = {
  show: PropTypes.bool.isRequired,
  plan: PropTypes.object,
  traveler: PropTypes.object,
  search: PropTypes.object,
  docs: PropTypes.object,
  submitting: PropTypes.bool,
  errorMsg: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
  onHide: PropTypes.func.isRequired,
};

EsimReviewModal.defaultProps = {
  plan: null,
  traveler: null,
  search: null,
  docs: {},
  submitting: false,
  errorMsg: '',
};

export default EsimReviewModal;
